'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
}

const demoCredentials: DemoCredential[] = [
  { role: 'Criador', email: 'miguel.forge@gameforge.io', password: 'GameForge2026!' },
  { role: 'Admin', email: 'admin@gameforge.io', password: 'Admin#Forge2026' },
];

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | null>(null);
  const { signIn, signInWithGoogle } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Bem-vindo de volta!');
      router.push('/dashboard');
      router.refresh();
    } catch (err: any) {
      setIsLoading(false);
      const msg = err?.message || '';
      if (msg.includes('Invalid login credentials') || msg.includes('invalid_credentials')) {
        setError('email', { message: 'Email/utilizador ou password incorretos' });
      } else if (msg.includes('Email not confirmed') || msg.includes('email_not_confirmed')) {
        setError('email', {
          message: 'Email ainda não confirmado. Verifica a tua caixa de entrada e clica no link de ativação.',
        });
      } else if (msg.includes('Too many requests')) {
        setError('email', { message: 'Demasiadas tentativas. Aguarda alguns minutos.' });
      } else {
        setError('email', { message: msg || 'Erro ao entrar. Tenta novamente.' });
      }
    }
  };

  const handleOAuthLogin = async () => {
    setOauthLoading('google');
    try {
      await signInWithGoogle();
      // Redirect happens automatically via OAuth flow
    } catch (err: any) {
      setOauthLoading(null);
      toast.error(err?.message || 'Erro ao entrar com Google');
    }
  };

  const handleForgotPassword = async () => {
    const email = (document.getElementById('login-email') as HTMLInputElement)?.value;
    if (!email) {
      toast.info('Introduz o teu email no campo acima primeiro.');
      return;
    }
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin;
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${siteUrl}/auth/callback?next=/dashboard`,
      });
      if (error) throw error;
      toast.success('Email de recuperação enviado! Verifica a tua caixa de entrada.');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao enviar email de recuperação');
    }
  };

  const fillCredentials = (cred: DemoCredential) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-foreground mb-1">Bem-vindo de volta</h2>
        <p className="text-sm text-muted-foreground">Entra na tua conta Core Forges</p>
      </div>

      {/* Social auth */}
      <div className="mb-6">
        <button
          type="button"
          onClick={handleOAuthLogin}
          disabled={oauthLoading !== null || isLoading}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-muted/50 border border-border hover:bg-muted hover:border-border/80 transition-all duration-150 text-sm font-medium text-foreground active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {oauthLoading === 'google' ? (
            <Icon name="ArrowPathIcon" size={16} className="animate-spin text-muted-foreground" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          )}
          Continuar com Google
        </button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">ou com email</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Email or Username */}
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium text-foreground mb-1.5">
            Email ou nome de utilizador
          </label>
          <input
            id="login-email"
            type="text"
            autoComplete="username"
            placeholder="email@exemplo.com ou username"
            className={`
              w-full px-4 py-2.5 rounded-xl bg-input border text-sm text-foreground placeholder:text-muted-foreground
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-150
              ${errors.email ? 'border-danger focus:ring-danger/50' : 'border-border'}
            `}
            {...register('email', {
              required: 'O email ou nome de utilizador é obrigatório',
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="login-password" className="block text-sm font-medium text-foreground">
              Password
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-xs text-primary hover:text-primary/80 transition-colors"
            >
              Esqueceste a password?
            </button>
          </div>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••"
              className={`
                w-full px-4 py-2.5 pr-11 rounded-xl bg-input border text-sm text-foreground placeholder:text-muted-foreground
                focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-150
                ${errors.password ? 'border-danger focus:ring-danger/50' : 'border-border'}
              `}
              {...register('password', {
                required: 'A password é obrigatória',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-danger">{errors.password.message}</p>
          )}
        </div>

        {/* Remember me */}
        <div className="flex items-center gap-2">
          <input
            id="remember-me"
            type="checkbox"
            className="w-4 h-4 rounded border-border bg-input text-primary focus:ring-primary/50 cursor-pointer"
            {...register('rememberMe')}
          />
          <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">
            Lembrar-me durante 30 dias
          </label>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading || oauthLoading !== null}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
          style={{ minHeight: '44px' }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              A entrar...
            </>
          ) : (
            'Entrar na conta'
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Ainda não tens conta?{' '}
        <button onClick={onSwitchToSignup} className="text-primary hover:text-primary/80 font-medium transition-colors">
          Cria uma gratuitamente
        </button>
      </p>

      {/* Demo credentials */}
      <div className="mt-8 p-4 rounded-xl bg-muted/30 border border-border">
        <p className="text-xs font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
          <Icon name="InformationCircleIcon" size={14} />
          Contas de demonstração
        </p>
        <div className="space-y-2">
          {demoCredentials.map((cred) => (
            <button
              key={cred.role}
              type="button"
              onClick={() => fillCredentials(cred)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted border border-border/50 hover:border-border transition-all duration-150 text-left"
            >
              <div>
                <p className="text-xs font-semibold text-foreground">{cred.role}</p>
                <p className="text-xs text-muted-foreground">{cred.email}</p>
              </div>
              <Icon name="ArrowRightIcon" size={12} className="text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}